/**
 * "Preview mode" download nudge. Shown once per presentation (or unsaved
 * draft) in the current tab session — not persisted across tab closes.
 *
 * Saved collections are keyed by collection id. Unsaved builder work uses a
 * draft id that rotates when the user starts a new presentation, and also on
 * page remount (builder state is React-only and wiped by reload, so a fresh
 * draft scope avoids inheriting a stale "seen" flag).
 */

export const PREVIEW_MODE_HINT_KEY_PREFIX = "slide-sense-preview-mode-seen:"
export const PREVIEW_MODE_DRAFT_SCOPE_KEY = "slide-sense-preview-mode-draft-scope"
const LEGACY_LOCAL_STORAGE_KEY = "slide-sense-preview-mode-seen"

function newDraftScopeId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `draft:${crypto.randomUUID()}`
  }
  return `draft:${Date.now()}`
}

function storageKey(scopeId: string): string {
  return `${PREVIEW_MODE_HINT_KEY_PREFIX}${scopeId}`
}

function collectionScopeId(collectionId: string): string {
  return `collection:${collectionId}`
}

function clearLegacyLocalStorageFlag(): void {
  try {
    window.localStorage.removeItem(LEGACY_LOCAL_STORAGE_KEY)
  } catch {
    // Ignore — private mode / quota.
  }
}

export function getOrCreateDraftPreviewHintScopeId(): string {
  try {
    const existing = window.sessionStorage.getItem(PREVIEW_MODE_DRAFT_SCOPE_KEY)
    if (existing) return existing
    const created = newDraftScopeId()
    window.sessionStorage.setItem(PREVIEW_MODE_DRAFT_SCOPE_KEY, created)
    return created
  } catch {
    return newDraftScopeId()
  }
}

/** Mint a new unsaved-presentation scope so the next Preview click can nudge again. */
export function rotateDraftPreviewHintScopeId(): string {
  const created = newDraftScopeId()
  try {
    window.sessionStorage.setItem(PREVIEW_MODE_DRAFT_SCOPE_KEY, created)
  } catch {
    // Private mode / quota — caller still gets a fresh in-memory id.
  }
  return created
}

export function previewHintScopeId(collectionId: string | null | undefined): string {
  if (collectionId) return collectionScopeId(collectionId)
  return getOrCreateDraftPreviewHintScopeId()
}

export function hasSeenPreviewModeHint(scopeId: string): boolean {
  clearLegacyLocalStorageFlag()
  try {
    return window.sessionStorage.getItem(storageKey(scopeId)) === "1"
  } catch {
    return false
  }
}

export function markPreviewModeHintSeen(scopeId: string): void {
  clearLegacyLocalStorageFlag()
  try {
    window.sessionStorage.setItem(storageKey(scopeId), "1")
  } catch {
    // Private mode / quota — show the hint again next time.
  }
}

/** Carry a dismissed draft nudge onto the collection created from that draft. */
export function transferPreviewHintSeen(fromScopeId: string, toScopeId: string): void {
  if (fromScopeId === toScopeId) return
  if (hasSeenPreviewModeHint(fromScopeId)) markPreviewModeHintSeen(toScopeId)
}
